import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Gravity WebGL scene — adapted from the Getlayers Gravity template.
// Renders a floating field of glassy pastel spheres that react to the cursor.
// Optimised for a hero background (no scroll-driven multi-section behavior).

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smoothstep = (a, b, x) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const lerp = (a, b, t) => a + (b - a) * t;

export default function FestivityCanvas({ ballColor = '#E9298A' }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const mousePosRef = useRef({ x: 99, y: 99, isDown: false });

  useEffect(() => {
    if (!containerRef.current || !canvasRef.current) return;
    const container = containerRef.current;
    const canvas = canvasRef.current;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, container.clientWidth / container.clientHeight, 0.1, 100);
    camera.position.set(0, 0, 11);

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.15;

    // Lights
    const hemi = new THREE.HemisphereLight(0xffffff, new THREE.Color(ballColor), 1.6);
    scene.add(hemi);
    const key = new THREE.DirectionalLight(0xffffff, 1.4);
    key.position.set(-6, 10, 8);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0xffffff, 1.25);
    rim.position.set(8, 7, -8);
    scene.add(rim);
    const front = new THREE.DirectionalLight(0xffffff, 0.4);
    front.position.set(0, 0, 11);
    scene.add(front);

    // Frustum bounds
    let viewportWidth = 10;
    let viewportHeight = 6;
    const updateBounds = () => {
      const fovRad = THREE.MathUtils.degToRad(camera.fov);
      viewportHeight = 2 * Math.tan(fovRad / 2) * camera.position.z;
      viewportWidth = viewportHeight * (container.clientWidth / container.clientHeight);
    };
    updateBounds();

    const isMobile = window.innerWidth < 768;
    const ballCount = isMobile ? 38 : 72;

    // Palette derived from ballColor — pink primary + yellow accent + neutrals.
    const palette = {
      pastel: new THREE.Color('#FFF3E9'),
      light: new THREE.Color('#FFE600'),
      medium: new THREE.Color(ballColor),
      deep: new THREE.Color('#0A0A0A'),
      glass: new THREE.Color('#FFC5D9'),
    };

    const sphereGeom = new THREE.SphereGeometry(1, 40, 40);
    const balls = [];
    for (let i = 0; i < ballCount; i++) {
      let radius = 0.33;
      const r = Math.random();
      if (r < 0.3) radius = 0.24 + Math.random() * 0.1;
      else if (r < 0.8) radius = 0.38 + Math.random() * 0.16;
      else radius = 0.58 + Math.random() * 0.18;

      const mass = Math.pow(radius, 3);
      let material;
      let color;

      const isGlass = Math.random() < 0.28;
      if (isGlass) {
        material = new THREE.MeshPhysicalMaterial({
          color: palette.glass,
          roughness: 0.08,
          metalness: 0,
          clearcoat: 1,
          clearcoatRoughness: 0.03,
          transmission: 0.92,
          ior: 1.485,
          thickness: 2.2,
          specularColor: new THREE.Color('#ffffff'),
          specularIntensity: 1.0,
          attenuationColor: palette.pastel,
          attenuationDistance: 1.0,
          emissive: palette.glass,
          emissiveIntensity: 0.1,
          transparent: true,
        });
        color = palette.glass;
      } else {
        const cr = Math.random();
        color = cr < 0.3 ? palette.pastel : cr < 0.6 ? palette.light : cr < 0.9 ? palette.medium : palette.deep;
        material = new THREE.MeshPhysicalMaterial({
          color,
          roughness: 0.44,
          metalness: 0,
          clearcoat: 0.24,
          clearcoatRoughness: 0.35,
          emissive: color,
          emissiveIntensity: 0.08,
          transparent: true,
        });
      }

      const group = new THREE.Group();
      const mesh = new THREE.Mesh(sphereGeom, material);
      mesh.scale.setScalar(radius);
      group.add(mesh);
      scene.add(group);

      // Random start inside viewport
      const px = (Math.random() * 2 - 1) * viewportWidth * 0.45;
      const py = (Math.random() * 2 - 1) * viewportHeight * 0.4;
      const pz = (Math.random() - 0.5) * 2;
      const position = new THREE.Vector3(px, py, pz);
      const velocity = new THREE.Vector3(
        (Math.random() - 0.5) * 0.02,
        (Math.random() - 0.5) * 0.02,
        (Math.random() - 0.5) * 0.01
      );
      group.position.copy(position);
      balls.push({ id: i, radius, mass, position, velocity, group, mesh, material });
    }

    const params = {
      rebound: -0.35,
      mouseRepelForce: 0.045,
      mouseRepelRadius: 4.0,
      damping: 0.94,
      centerAttractForce: 0.004,
      bounciness: 0.05,
    };

    const mouseProj = new THREE.Vector3();
    const mouseWorld = new THREE.Vector3();
    const updateMouse3D = () => {
      mouseProj.set(mousePosRef.current.x, mousePosRef.current.y, 0.5);
      mouseProj.unproject(camera);
      const dir = mouseProj.sub(camera.position).normalize();
      const distance = -camera.position.z / dir.z;
      mouseWorld.copy(camera.position).add(dir.multiplyScalar(distance));
    };

    // Pointer tracking (attached to window so it works even though the container is pointer-events: none)
    const onMove = (e) => {
      mousePosRef.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      mousePosRef.current.y = -(e.clientY / window.innerHeight) * 2 + 1;
    };
    const onDown = () => { mousePosRef.current.isDown = true; };
    const onUp = () => { mousePosRef.current.isDown = false; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);

    let raf = 0;
    const clock = new THREE.Clock();
    const diff = new THREE.Vector3();
    const colDiff = new THREE.Vector3();
    const relVel = new THREE.Vector3();
    const deltaPos = new THREE.Vector3();
    const rotAxis = new THREE.Vector3();
    const prevMouse = new THREE.Vector3();
    let mouseSpeed = 0;

    const loop = () => {
      raf = requestAnimationFrame(loop);
      const t = clock.getElapsedTime();
      updateMouse3D();

      const isHover = Math.abs(mousePosRef.current.x) < 0.99 || Math.abs(mousePosRef.current.y) < 0.99;
      mouseSpeed = isHover ? mouseWorld.distanceTo(prevMouse) : 0;
      if (mouseSpeed > 3) mouseSpeed = 3;
      prevMouse.copy(mouseWorld);

      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        b.velocity.x += Math.sin(t * 0.4 + b.id * 1.5) * 0.0004 * b.radius;
        b.velocity.y += Math.cos(t * 0.5 + b.id * 1.2) * 0.0004 * b.radius;

        // Cluster spring
        b.velocity.x += (0 - b.position.x) * params.centerAttractForce * 0.4;
        b.velocity.y += (0 - b.position.y) * params.centerAttractForce * 1.6;
        b.velocity.z += (0 - b.position.z) * params.centerAttractForce * 1.6;

        if (isHover) {
          diff.subVectors(b.position, mouseWorld);
          const d = diff.length();
          const down = mousePosRef.current.isDown;
          const rad = down ? params.mouseRepelRadius * 1.35 : params.mouseRepelRadius;
          const frc = down ? params.mouseRepelForce * 1.6 : params.mouseRepelForce;
          if (d < rad && d > 0.0001) {
            const ratio = d / rad;
            const s = 1.0 - ratio * ratio * (3 - 2 * ratio);
            const boost = 1 + mouseSpeed * 3.0;
            diff.normalize();
            diff.z *= 0.15;
            diff.normalize();
            b.velocity.addScaledVector(diff, s * frc * boost);
          }
        }

        b.velocity.multiplyScalar(params.damping);
        b.position.addScaledVector(b.velocity, 1);
      }

      // Pairwise collisions (fewer sub-steps for perf)
      const subSteps = 2;
      for (let s = 0; s < subSteps; s++) {
        for (let i = 0; i < balls.length; i++) {
          for (let j = i + 1; j < balls.length; j++) {
            const a = balls[i];
            const c = balls[j];
            colDiff.subVectors(c.position, a.position);
            const dist = colDiff.length();
            const minDist = a.radius + c.radius;
            if (dist < minDist && dist > 0.001) {
              const overlap = minDist - dist;
              colDiff.multiplyScalar(1 / dist);
              const tm = a.mass + c.mass;
              a.position.addScaledVector(colDiff, -overlap * (c.mass / tm) * 0.3);
              c.position.addScaledVector(colDiff, overlap * (a.mass / tm) * 0.3);
              relVel.subVectors(c.velocity, a.velocity);
              const van = relVel.dot(colDiff);
              if (van < -0.0001) {
                const imp = -(1 + params.bounciness) * van / (1 / a.mass + 1 / c.mass);
                a.velocity.addScaledVector(colDiff, -imp / a.mass);
                c.velocity.addScaledVector(colDiff, imp / c.mass);
              }
            }
          }
        }
      }

      // Viewport walls
      const pad = 0.2;
      const xB = viewportWidth / 2 - pad;
      const yB = viewportHeight / 2 - pad;
      const zB = 2.0;
      for (const b of balls) {
        const r = b.radius;
        if (b.position.x < -xB - r) { b.position.x = -xB - r; b.velocity.x *= params.rebound; }
        else if (b.position.x > xB + r) { b.position.x = xB + r; b.velocity.x *= params.rebound; }
        if (b.position.y < -yB - r) { b.position.y = -yB - r; b.velocity.y *= params.rebound; }
        else if (b.position.y > yB + r) { b.position.y = yB + r; b.velocity.y *= params.rebound; }
        if (b.position.z < -zB) { b.position.z = -zB; b.velocity.z *= params.rebound; }
        else if (b.position.z > zB) { b.position.z = zB; b.velocity.z *= params.rebound; }

        deltaPos.copy(b.position).sub(b.group.position);
        if (deltaPos.lengthSq() > 1e-6) {
          rotAxis.set(deltaPos.y, -deltaPos.x, 0).normalize();
          const ang = (deltaPos.length() / b.radius) * 0.9;
          b.group.rotateOnWorldAxis(rotAxis, ang);
        }
        b.group.position.copy(b.position);
      }

      renderer.render(scene, camera);
    };
    loop();

    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      updateBounds();
    };
    const ro = new ResizeObserver(handleResize);
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      balls.forEach((b) => {
        b.group.traverse((n) => {
          if (n.isMesh) {
            n.geometry.dispose && n.geometry.dispose();
            if (Array.isArray(n.material)) n.material.forEach((m) => m.dispose());
            else n.material.dispose();
          }
        });
      });
      sphereGeom.dispose();
      renderer.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full pointer-events-none select-none">
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
}
